import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  IoAlertCircle,
  IoArrowDown,
  IoSaveOutline,
  IoTrashOutline,
} from 'react-icons/io5';

import AdmissionPanel from './AdmissionPanel';
import SettingsEditor from './SettingsEditor';
import { confirmDialog } from '../../components/ConfirmDialog';
import { pipelineApi } from '../../services/api';
import { useAccess } from '../../services/access';

// Admin → AI Pipeline. How the meeting pipeline runs, and the three things
// that tune it without a deploy: prompts (text the model is sent), rubrics
// (how each judgment is scored) and numbers (application-wide settings).
// Every change is a new version; records keep the version they were made
// under, so a published change only affects meetings processed after it.
// Reading needs admin:config:view; changing anything needs :manage.

// The pipeline's stages, in run order. `prompts` / `rubrics` name the
// registry keys a stage uses; numbers come from the catalogue by stage id.
const STAGES = [
  {
    id: '0a',
    name: 'Segment',
    does: 'Splits the transcript into speaker turns, each with a stable seg_id that every record quotes.',
  },
  {
    id: '0b',
    name: 'Normalize',
    does: 'Cleans speaker names, project terms and timestamps.',
  },
  {
    id: '0c',
    name: 'Prefetch',
    does: 'Pulls prior meetings, the glossary, people profiles and tickets as context.',
  },
  {
    id: '1',
    name: 'Assemble context',
    does: 'Packs transcript, context and the rubric text into the budget each model call gets.',
    rubrics: ['capture', 'risk', 'admission', 'decision_confidence'],
  },
  {
    id: '2',
    name: 'Extract',
    does: 'Five model calls in parallel: commitments, concerns, decisions, change events, person observations. Each sends the shared preamble plus its own prompt.',
    prompts: [
      'preamble',
      'commitments',
      'concerns',
      'decisions',
      'change_events',
      'person_observations',
    ],
  },
  {
    id: '3',
    name: 'Reconcile',
    does: 'Matches new candidates against records from earlier meetings instead of creating duplicates.',
  },
  {
    id: '4a',
    name: 'Validate',
    does: 'Checks every quote against its segment and strips fields the model is not allowed to write.',
  },
  {
    id: '4b',
    name: 'Compute',
    does: 'Code (not the model) computes confidence and risk scores and decides which risks reach the register.',
    rubrics: ['capture', 'risk', 'admission'],
    admission: true,
  },
  {
    id: '5',
    name: 'Commit',
    does: 'Writes the records, each stamped with the prompt, rubric and config version it was produced under.',
  },
  {
    id: '6',
    name: 'Tracker (hourly)',
    does: 'Follows open commitments and proposes reminders to people.',
  },
];

const SAFETY = {
  TUNABLE: {
    tone: 'bg-emerald-500/10 text-emerald-400',
    text: 'Move freely. Affects future meetings only; stored data stays comparable.',
  },
  SENSITIVE: {
    tone: 'bg-amber-500/10 text-amber-400',
    text: 'Changes what future records mean, so old and new stop being directly comparable. A reason is required.',
  },
  BREAKING: {
    tone: 'bg-red-500/10 text-red-400',
    text: 'Would invalidate stored data. Shown for visibility; needs a migration, not a setting.',
  },
};

const STATUS_TONE = {
  DRAFT: 'bg-sky-500/10 text-sky-400',
  PUBLISHED: 'bg-emerald-500/10 text-emerald-400',
  SUPERSEDED: 'bg-zinc-700/50 text-zinc-400',
  ARCHIVED: 'bg-zinc-800 text-zinc-500',
};

// Fields a rubric draft accepts (RubricDraft on the server).
const RUBRIC_FIELDS = [
  'mode',
  'scale',
  'basis_policy',
  'factors',
  'factor_groups',
  'applies_to',
];

const input =
  'w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:border-indigo-500 focus:outline-none';
const button =
  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:opacity-50';
const primary = `${button} bg-indigo-600 text-white hover:bg-indigo-500`;
const secondary = `${button} border border-zinc-700 text-zinc-300 hover:bg-zinc-800`;
const danger = `${button} border border-red-900 text-red-400 hover:bg-red-950`;
const badge = 'rounded-full px-2 py-0.5 text-xs';
const mono = 'font-mono text-xs';

const errorText = (error) =>
  (Array.isArray(error) && error.map((e) => e.msg || e).join('; ')) ||
  (typeof error === 'string' && error) ||
  error?.message ||
  'Something went wrong';

const fail = (error) => toast.error(errorText(error));

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');

// The version live for everyone: published, global scope.
const liveOf = (docs, field, key) =>
  docs.find(
    (d) =>
      d[field] === key && d.status === 'PUBLISHED' && d.scope !== 'PROJECT',
  );

const draftOf = (docs, field, key) =>
  docs.find(
    (d) => d[field] === key && d.status === 'DRAFT' && d.scope !== 'PROJECT',
  );

// Next free "vN" for a key; v1 is the in-code version.
const nextVersion = (docs, field, key) => {
  const used = docs
    .filter((d) => d[field] === key)
    .map((d) => Number(/^v(\d+)$/.exec(d.version)?.[1] || 0));
  return `v${Math.max(1, ...used) + 1}`;
};

const Section = ({ title, hint, children, action }) => (
  <section className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-5">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
        {hint && <p className="mt-1 text-sm text-zinc-500">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Status = ({ status }) => (
  <span className={`${badge} ${STATUS_TONE[status] || STATUS_TONE.ARCHIVED}`}>
    {status}
  </span>
);

const Notice = ({ children, tone = 'amber' }) => (
  <div
    className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
      tone === 'amber'
        ? 'border-amber-900/60 bg-amber-950/30 text-amber-300'
        : 'border-zinc-800 bg-zinc-900/60 text-zinc-400'
    }`}
  >
    <IoAlertCircle className="mt-0.5 shrink-0" />
    <div>{children}</div>
  </div>
);

// Left-hand key picker shared by the prompts and rubrics tabs.
const KeyList = ({ keys, selected, onSelect, describe }) => (
  <div className="space-y-1">
    {keys.map((key) => (
      <button
        key={key}
        onClick={() => onSelect(key)}
        className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
          key === selected
            ? 'bg-indigo-600/20 text-indigo-300'
            : 'text-zinc-300 hover:bg-zinc-800'
        }`}
      >
        <div className="font-medium">{key}</div>
        <div className="text-xs text-zinc-500">{describe(key)}</div>
      </button>
    ))}
  </div>
);

const VersionList = ({ docs, canManage, onArchive }) =>
  docs.length === 0 ? (
    <p className="text-sm text-zinc-500">
      No versions yet. The in-code version is running.
    </p>
  ) : (
    <ul className="divide-y divide-zinc-800 rounded-md border border-zinc-800">
      {docs.map((d) => (
        <li
          key={d._id}
          className="flex items-center justify-between gap-3 px-3 py-2"
        >
          <div className="min-w-0">
            <span className={`${mono} text-zinc-200`}>{d._id}</span>{' '}
            <Status status={d.status} />
            <div className="text-xs text-zinc-500">
              {d.published_at
                ? `Published ${formatDate(d.published_at)}`
                : `Created ${formatDate(d.created_at)}`}
              {d.notes ? ` · ${d.notes}` : ''}
            </div>
          </div>
          {canManage && ['PUBLISHED', 'SUPERSEDED'].includes(d.status) && (
            <button className={secondary} onClick={() => onArchive(d._id)}>
              Archive
            </button>
          )}
        </li>
      ))}
    </ul>
  );

const Chip = ({ label, live, onClick }) => (
  <button
    onClick={onClick}
    className={`${badge} border ${
      live
        ? 'border-indigo-700 text-indigo-300'
        : 'border-zinc-700 text-zinc-400'
    } hover:bg-zinc-800`}
    title={
      live ? `Custom version live: ${live}` : 'Running the in-code version'
    }
  >
    {label}
    {live ? ` · ${live}` : ''}
  </button>
);

// ---------------------------------------------------------------------------
// Overview: what runs, in order, and what each control changes
// ---------------------------------------------------------------------------

const Overview = ({ data, goTo }) => {
  const { catalogue, prompts, rubrics } = data;
  const numbersFor = (stageId) =>
    catalogue.items.filter((item) => item.stage.split(' ')[0] === stageId);

  const customPrompts = new Set(
    prompts.filter((p) => p.status === 'PUBLISHED').map((p) => p.prompt_key),
  );
  const customRubrics = new Set(
    rubrics.filter((r) => r.status === 'PUBLISHED').map((r) => r.rubric_key),
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-zinc-800 bg-zinc-800 md:grid-cols-4">
        {[
          [STAGES.length, 'stages'],
          [`${customPrompts.size}/6`, 'prompts customised'],
          [customRubrics.size, 'rubrics customised'],
          [catalogue.count, 'numeric settings'],
        ].map(([value, label]) => (
          <div key={label} className="bg-zinc-950 px-4 py-3">
            <div className="text-2xl font-semibold text-zinc-100">{value}</div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              {label}
            </div>
          </div>
        ))}
      </div>

      <Section
        title="How a meeting is processed"
        hint="Each transcript runs these stages in order. Chips show what you can change at each step; highlighted chips are running a custom version instead of the one in code."
      >
        <ol className="space-y-2">
          {STAGES.map((stage, index) => {
            const numbers = numbersFor(stage.id);
            const risky = numbers.filter((n) => n.safety !== 'TUNABLE').length;
            return (
              <li key={stage.id}>
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
                  <div className="flex items-baseline gap-3">
                    <span className={`${mono} w-8 shrink-0 text-indigo-400`}>
                      {stage.id}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-200">
                        {stage.name}
                      </div>
                      <p className="text-sm text-zinc-400">{stage.does}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(stage.prompts || []).map((key) => (
                          <Chip
                            key={key}
                            label={`prompt: ${key}`}
                            live={liveOf(prompts, 'prompt_key', key)?.version}
                            onClick={() => goTo('prompts', key)}
                          />
                        ))}
                        {(stage.rubrics || []).map((key) => (
                          <Chip
                            key={key}
                            label={`rubric: ${key}`}
                            live={liveOf(rubrics, 'rubric_key', key)?.version}
                            onClick={() => goTo('rubrics', key)}
                          />
                        ))}
                        {stage.admission && (
                          <Chip
                            label="admission policy"
                            onClick={() => goTo('admission')}
                          />
                        )}
                        {numbers.length > 0 && (
                          <Chip
                            label={`${numbers.length} numeric setting${numbers.length > 1 ? 's' : ''}${risky ? ` (${risky} sensitive/breaking)` : ''}`}
                            onClick={() => goTo('numbers')}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {index < STAGES.length - 1 && (
                  <IoArrowDown className="mx-auto my-1 text-zinc-600" />
                )}
              </li>
            );
          })}
        </ol>
      </Section>

      <Section title="What a change affects">
        <ul className="space-y-2 text-sm text-zinc-400">
          <li>
            <b className="text-zinc-200">Prompts</b> change what the model is
            asked in stage 2. Live on the next extraction call. Guardrail
            sentences (&quot;untrusted&quot;, &quot;NEVER EMIT&quot;) are
            required; a prompt without them is refused.
          </li>
          <li>
            <b className="text-zinc-200">Rubrics</b> change how each judgment is
            scored: the text is sent with every extraction (stage 1) and the
            numbers are recomputed in stage 4b. Run a dry run first: it shows
            how many stored risks would cross the admission line.
          </li>
          <li>
            <b className="text-zinc-200">Numbers</b> apply to every project.
            Each one states what it moves downstream and how safe it is to move.
          </li>
          <li>
            Every record stores the prompt, rubric and config version it was
            made under, so publishing never rewrites history. To go back,
            archive the custom version and the in-code one runs again.
          </li>
          <li>
            Run-time metrics for every stage (durations, model calls, defects)
            go to Prometheus/Grafana.
          </li>
        </ul>
        <div className="mt-4 grid gap-2 md:grid-cols-3">
          {Object.entries(SAFETY).map(([name, { tone, text }]) => (
            <div key={name} className="rounded-md bg-zinc-900/60 p-3">
              <span className={`${badge} ${tone}`}>{name}</span>
              <p className="mt-1 text-xs text-zinc-400">{text}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rubrics
// ---------------------------------------------------------------------------

const pickRubricFields = (doc) =>
  Object.fromEntries(
    RUBRIC_FIELDS.filter((f) => doc?.[f] !== undefined).map((f) => [f, doc[f]]),
  );

const Rubrics = ({ token, data, canManage, reload, initialKey }) => {
  const keys = useMemo(
    () => [
      ...new Set([
        ...data.seeds.map((s) => s.rubric_key),
        ...data.rubrics.map((r) => r.rubric_key),
      ]),
    ],
    [data],
  );
  const [key, setKey] = useState(initialKey || keys[0]);
  const [text, setText] = useState('');
  const [shift, setShift] = useState(null);
  const [busy, setBusy] = useState(false);

  const docs = data.rubrics.filter((r) => r.rubric_key === key);
  const live = liveOf(data.rubrics, 'rubric_key', key);
  const draft = draftOf(data.rubrics, 'rubric_key', key);
  const seed = data.seeds.find((s) => s.rubric_key === key);
  const source = draft || live || seed;
  // Flat (factors) or grouped (factor_groups, e.g. risk) rubrics.
  const draftable = Boolean(
    (live || seed)?.factors || (live || seed)?.factor_groups,
  );

  useEffect(() => {
    setShift(null);
    setText(
      JSON.stringify(draft ? pickRubricFields(draft) : source || {}, null, 2),
    );
  }, [draft, source]);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const parsed = () => {
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error(`Not valid JSON: ${error.message}`);
    }
  };

  const asDraft = () => ({
    rubric_key: key,
    version: draft.version,
    scope: 'GLOBAL',
    ...pickRubricFields(parsed()),
  });

  const start = () =>
    run(async () => {
      await pipelineApi.createRubricDraft(token, {
        rubric_key: key,
        version: nextVersion(data.rubrics, 'rubric_key', key),
        scope: 'GLOBAL',
        ...pickRubricFields(live || seed),
      });
      await reload();
    }, 'Draft created');

  const save = () =>
    run(async () => {
      await pipelineApi.saveRubricDraft(
        token,
        draft._id,
        pickRubricFields(parsed()),
      );
      await reload();
    }, 'Draft saved');

  const dryRun = () =>
    run(async () =>
      setShift((await pipelineApi.dryRunRubric(token, asDraft())).shift),
    );

  const publish = () =>
    run(async () => {
      const ok = await confirmDialog({
        title: `Publish ${key}@${draft.version}?`,
        message:
          'Every record scored from now on uses this rubric. Stored scores keep the version they were scored under. Run a dry run first if you have not.',
        confirmLabel: 'Publish',
      });
      if (!ok) return;
      await pipelineApi.publishRubric(token, asDraft());
      await reload();
      toast.success('Published.');
    });

  const discard = () =>
    run(async () => {
      await pipelineApi.deleteRubricDraft(token, draft._id);
      await reload();
    }, 'Draft discarded');

  const archive = (ref) =>
    run(async () => {
      const ok = await confirmDialog({
        title: `Archive ${ref}?`,
        message:
          'If this is the live version, scoring falls back to the in-code rubric. Stored scores keep pointing at it.',
        confirmLabel: 'Archive',
        danger: true,
      });
      if (!ok) return;
      await pipelineApi.archiveRubric(token, ref);
      await reload();
    });

  const editable = canManage && Boolean(draft);

  return (
    <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
      <KeyList
        keys={keys}
        selected={key}
        onSelect={setKey}
        describe={(k) => {
          const l = liveOf(data.rubrics, 'rubric_key', k);
          const d = draftOf(data.rubrics, 'rubric_key', k);
          return `${l ? `live ${l.version}` : 'in-code'}${d ? ` · draft ${d.version}` : ''}`;
        }}
      />
      <div className="space-y-4">
        <Section
          title={key}
          hint={
            live
              ? `Live: ${live._id}, published ${formatDate(live.published_at)}`
              : `Live: the in-code rubric${seed ? ` (${seed._id})` : ''}.`
          }
          action={
            canManage &&
            !draft &&
            draftable && (
              <button className={primary} disabled={busy} onClick={start}>
                Start a draft from this
              </button>
            )
          }
        >
          {draft && (
            <p className="mb-2 text-sm text-sky-300">
              Editing draft {draft._id} (JSON). Not used until published.
            </p>
          )}
          <textarea
            className={`${input} ${mono} h-96`}
            value={text}
            readOnly={!editable}
            onChange={(e) => setText(e.target.value)}
          />
          {editable && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button className={secondary} disabled={busy} onClick={save}>
                <IoSaveOutline /> Save draft
              </button>
              <button className={secondary} disabled={busy} onClick={dryRun}>
                Dry run on stored records
              </button>
              <button className={primary} disabled={busy} onClick={publish}>
                Publish
              </button>
              <button className={danger} disabled={busy} onClick={discard}>
                <IoTrashOutline /> Discard
              </button>
            </div>
          )}
          {shift && (
            <div className="mt-3 grid gap-px overflow-hidden rounded-md border border-zinc-800 bg-zinc-800 text-sm md:grid-cols-4">
              {[
                ['Records sampled', shift.sampled_record_count],
                [
                  'Mean score',
                  shift.mean_score_before == null
                    ? 'n/a'
                    : `${shift.mean_score_before.toFixed(3)} → ${shift.mean_score_after.toFixed(3)}`,
                ],
                ['Newly above threshold', shift.newly_above_threshold],
                ['Newly below threshold', shift.newly_below_threshold],
              ].map(([label, value]) => (
                <div key={label} className="bg-zinc-950 px-3 py-2">
                  <div className="text-xs text-zinc-500">{label}</div>
                  <div className="text-zinc-200">{value}</div>
                </div>
              ))}
              {shift.note && (
                <div className="bg-zinc-950 px-3 py-2 text-xs text-zinc-400 md:col-span-4">
                  {shift.note}
                </div>
              )}
            </div>
          )}
        </Section>
        <Section title="Versions">
          <VersionList docs={docs} canManage={canManage} onArchive={archive} />
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

export {
  SAFETY,
  Notice,
  Section,
  VersionList,
  badge,
  danger,
  draftOf,
  errorText,
  formatDate,
  input,
  liveOf,
  mono,
  nextVersion,
  primary,
  secondary,
};

const TABS = [
  ['overview', 'Overview'],
  ['rubrics', 'Rubrics'],
  ['admission', 'Admission'],
  ['numbers', 'Numbers'],
];

export default function PipelineSettingsPanel({ userIdToken, onOpenPrompt }) {
  const access = useAccess();
  const canManage = access.can('admin:config:manage');
  const [tab, setTab] = useState({ name: 'overview' });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [catalogue, prompts, rubrics, defaults] = await Promise.all([
      pipelineApi.catalogue(userIdToken),
      pipelineApi.prompts(userIdToken),
      pipelineApi.rubrics(userIdToken),
      pipelineApi.rubricDefaults(userIdToken),
    ]);
    setData({
      catalogue,
      catalogueKeys: prompts.keys,
      prompts: prompts.prompts,
      rubrics: rubrics.rubrics,
      seeds: defaults.rubrics,
    });
  }, [userIdToken]);

  useEffect(() => {
    if (userIdToken) load().catch((e) => setError(errorText(e)));
  }, [userIdToken, load]);

  if (error) return <Notice>{error}</Notice>;
  if (!data) return <p className="text-sm text-zinc-500">Loading pipeline…</p>;

  const goTo = (name, key) =>
    name === 'prompts' ? onOpenPrompt?.(key) : setTab({ name, key });
  const props = {
    token: userIdToken,
    data,
    canManage,
    reload: load,
    initialKey: tab.key,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-lg bg-zinc-900 p-1">
          {TABS.map(([name, label]) => (
            <button
              key={name}
              onClick={() => goTo(name)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                tab.name === name
                  ? 'bg-zinc-700 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {!canManage && (
          <span className="text-sm text-zinc-500">
            View only: changing these needs the Manage Configuration permission.
          </span>
        )}
      </div>
      {tab.name === 'overview' && <Overview data={data} goTo={goTo} />}
      {tab.name === 'rubrics' && <Rubrics key={tab.key} {...props} />}
      {tab.name === 'admission' && (
        <AdmissionPanel token={userIdToken} canManage={canManage} />
      )}
      {tab.name === 'numbers' && (
        <SettingsEditor
          token={userIdToken}
          area="meetings"
          canManage={canManage}
          groupLabel={(stage) => `Stage ${stage}`}
        />
      )}
    </div>
  );
}
